import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormCheckbox from '@components/UI/FormCheckbox';
import type { TradingProfile } from '@einhaus/project314-types';

interface EntryCollectionWindowCardProps {
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
}

const EntryCollectionWindowCard = ({ formData, setFormData }: EntryCollectionWindowCardProps) => {
    return (
        <Card className="mb-4" title="Entry Collection Window">
            <div className="mb-4 text-zinc-400">
                Configure how long to collect entries before processing them. This allows the system to compare and prioritize entries that
                arrive within the collection window.
            </div>

            {/* Enable/Disable Window */}
            <div className="flex items-center mb-4">
                <FormCheckbox
                    checked={!!formData.entryCollectionWindowConfig}
                    id="enableEntryCollectionWindow"
                    label="Enable Entry Collection Window"
                    onChange={(e) => {
                        const newConfig = e.target.checked
                            ? {
                                  windowSeconds: 60, // Default to 1 minute
                                  earlyProcessingTriggers: undefined,
                                  maxEntriesPerWindow: undefined
                              }
                            : null;

                        const updatedFormData = {
                            ...formData,
                            entryCollectionWindowConfig: newConfig
                        };

                        setFormData(updatedFormData);
                    }}
                />
            </div>

            {formData.entryCollectionWindowConfig && (
                <>
                    {/* Window Duration */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-6 flex items-center">
                            <label className="block text-sm font-medium text-zinc-200" htmlFor="windowSeconds">
                                Collection Window Duration (seconds)
                            </label>
                        </div>
                        <div className="md:col-span-6">
                            <FormNumberInput
                                integer
                                id="windowSeconds"
                                max={86400}
                                min={1}
                                value={formData.entryCollectionWindowConfig?.windowSeconds}
                                onValueChange={(value) => {
                                    // A window always has a duration, so an empty field keeps the stored value.
                                    if (value === null) return;

                                    const updatedFormData = {
                                        ...formData,
                                        entryCollectionWindowConfig: {
                                            ...formData.entryCollectionWindowConfig,
                                            windowSeconds: value
                                        }
                                    };

                                    setFormData(updatedFormData);
                                }}
                            />
                            <small className="text-zinc-500">Range: 1 second to 24 hours (86400 seconds)</small>
                        </div>
                    </div>

                    {/* Early Processing Triggers */}
                    <h5 className="mt-4 mb-3">Early Processing Triggers (Optional)</h5>
                    <div className="text-zinc-400 mb-3 text-sm">
                        Configure conditions that will trigger processing before the window expires.
                    </div>

                    {/* Minimum Score Trigger */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-6 flex items-center">
                            <label className="block text-sm font-medium text-zinc-200" htmlFor="minScore">
                                Minimum Score Threshold
                            </label>
                        </div>
                        <div className="md:col-span-6">
                            <FormNumberInput
                                id="minScore"
                                placeholder="Leave empty to disable"
                                step={0.1}
                                value={formData.entryCollectionWindowConfig?.earlyProcessingTriggers?.minScore}
                                onValueChange={(parsed) => {
                                    const value = parsed ?? undefined;

                                    if (!formData.entryCollectionWindowConfig) return;

                                    const updatedFormData = {
                                        ...formData,
                                        entryCollectionWindowConfig: {
                                            ...formData.entryCollectionWindowConfig,
                                            earlyProcessingTriggers: value
                                                ? {
                                                      ...formData.entryCollectionWindowConfig.earlyProcessingTriggers,
                                                      minScore: value
                                                  }
                                                : undefined
                                        }
                                    };

                                    // Clean up earlyProcessingTriggers if both values are undefined
                                    if (
                                        !updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers?.minScore &&
                                        !updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers?.entryCount
                                    ) {
                                        updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers = undefined;
                                    }

                                    setFormData(updatedFormData);
                                }}
                            />
                            <small className="text-zinc-500">Process immediately if any entry exceeds this score</small>
                        </div>
                    </div>

                    {/* Entry Count Trigger */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-6 flex items-center">
                            <label className="block text-sm font-medium text-zinc-200" htmlFor="entryCount">
                                Entry Count Threshold
                            </label>
                        </div>
                        <div className="md:col-span-6">
                            <FormNumberInput
                                integer
                                id="entryCount"
                                min={1}
                                placeholder="Leave empty to disable"
                                value={formData.entryCollectionWindowConfig?.earlyProcessingTriggers?.entryCount}
                                onValueChange={(parsed) => {
                                    const value = parsed ?? undefined;

                                    if (!formData.entryCollectionWindowConfig) return;

                                    const updatedFormData = {
                                        ...formData,
                                        entryCollectionWindowConfig: {
                                            ...formData.entryCollectionWindowConfig,
                                            earlyProcessingTriggers: value
                                                ? {
                                                      ...formData.entryCollectionWindowConfig.earlyProcessingTriggers,
                                                      entryCount: value
                                                  }
                                                : undefined
                                        }
                                    };

                                    // Clean up earlyProcessingTriggers if both values are undefined
                                    if (
                                        !updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers?.minScore &&
                                        !updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers?.entryCount
                                    ) {
                                        updatedFormData.entryCollectionWindowConfig.earlyProcessingTriggers = undefined;
                                    }

                                    setFormData(updatedFormData);
                                }}
                            />
                            <small className="text-zinc-500">Process when this many entries are collected</small>
                        </div>
                    </div>

                    {/* Max Entries Per Window */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-6 flex items-center">
                            <label className="block text-sm font-medium text-zinc-200" htmlFor="maxEntriesPerWindow">
                                Maximum Entries Per Window
                            </label>
                        </div>
                        <div className="md:col-span-6">
                            <FormNumberInput
                                integer
                                id="maxEntriesPerWindow"
                                min={1}
                                placeholder="Leave empty for unlimited"
                                value={formData.entryCollectionWindowConfig?.maxEntriesPerWindow}
                                onValueChange={(parsed) => {
                                    const value = parsed ?? undefined;

                                    if (!formData.entryCollectionWindowConfig) return;

                                    const updatedFormData = {
                                        ...formData,
                                        entryCollectionWindowConfig: {
                                            ...formData.entryCollectionWindowConfig,
                                            maxEntriesPerWindow: value
                                        }
                                    };

                                    setFormData(updatedFormData);
                                }}
                            />
                            <small className="text-zinc-500">Additional candidates are rejected to bound memory use</small>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-6 flex items-center">
                            <label className="block text-sm font-medium text-zinc-200" htmlFor="maxEntriesToSelect">
                                Entries Selected Per Window
                            </label>
                        </div>
                        <div className="md:col-span-6">
                            <FormNumberInput
                                integer
                                id="maxEntriesToSelect"
                                min={1}
                                placeholder="Leave empty to select every candidate"
                                value={formData.entryCollectionWindowConfig?.maxEntriesToSelect}
                                onValueChange={(parsed) => {
                                    const value = parsed ?? undefined;

                                    if (!formData.entryCollectionWindowConfig) return;

                                    setFormData({
                                        ...formData,
                                        entryCollectionWindowConfig: {
                                            ...formData.entryCollectionWindowConfig,
                                            maxEntriesToSelect: value
                                        }
                                    });
                                }}
                            />
                            <small className="text-zinc-500">Only the highest-ranked candidates proceed to portfolio admission</small>
                        </div>
                    </div>
                </>
            )}
        </Card>
    );
};

export default EntryCollectionWindowCard;
